import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Stack, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import publicApiClient from "../../../app/services/publicApiClient";

export default function ConnectErp({ onClose, open }) {

    const { i18n } = useTranslation();

    const [erpAdapters, setErpAdapters] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedAdapter, setSelectedAdapter] = useState(null);
    const [formValues, setFormValues] = useState({});
    const [isTesting, setIsTesting] = useState(false);
    const [connectionResult, setConnectionResult] = useState(null);
    const [connectionError, setConnectionError] = useState(null);

    useEffect(() => {
        if (!open) return undefined;

        let isMounted = true;
        setIsLoading(true);
        setError(null);
        setSelectedAdapter(null);
        setFormValues({});
        setConnectionResult(null);
        setConnectionError(null);

        publicApiClient.get("/erp/adapters")
        .then(({ data }) => {
            if (isMounted) setErpAdapters(Array.isArray(data) ? data : []);
        })
        .catch(() => {
            if (isMounted) setError("Järjestelmien hakeminen epäonnistui.");
        })
        .finally(() => {
            if (isMounted) setIsLoading(false);
        });

        return () => {
        isMounted = false;
        };
    }, [open]);

    const getDescription = (adapter) => adapter.description || {};

    const getLocalizedDescription = (adapter) => {
        const translations = getDescription(adapter).description?.translations || {};
        return translations[i18n.language] || translations.fi || translations.en || "ERP-integraatio";
    };

    const selectedDescription = selectedAdapter ? getDescription(selectedAdapter) : null;
    const selectedFields = selectedAdapter?.connectInformation?.fields || [];

    const selectAdapter = (adapter) => {
        const fields = adapter.connectInformation?.fields || [];
        const initialValues = fields.reduce((values, field) => ({
            ...values,
            [field.key]: field.defaultValue || "",
        }), {});

        setFormValues(initialValues);
        setSelectedAdapter(adapter);
        setConnectionResult(null);
        setConnectionError(null);
    };

    const requiredFieldsMissing = selectedFields.some((field) => field.required && !String(formValues[field.key] || "").trim());

    const getLocalizedError = (translations) => {
        const language = i18n.language?.split("-")[0];
        return translations?.[language] || translations?.en || translations?.fi || "Yhteyden testaaminen epäonnistui.";
    };

    const testConnection = async () => {
        if (!selectedAdapter || requiredFieldsMissing || isTesting) return;

        setIsTesting(true);
        setConnectionResult(null);
        setConnectionError(null);

        try {
            await publicApiClient.post("/erp/test-connection", {
                provider: selectedAdapter.provider,
                language: i18n.language?.split("-")[0],
                ...formValues,
            });
            setConnectionResult("Yhteys toimii.");
        } catch (requestError) {
            setConnectionError(getLocalizedError(requestError.response?.data?.translations));
        } finally {
            setIsTesting(false);
        }
    };

    return (
        <Dialog
        open={open}
        onClose={onClose}
        maxWidth='sm'
        fullWidth
        PaperProps={{
            sx: {
            borderRadius: "10px",
            width: "100%",
            maxWidth: 512,
            m: 2,
            },
        }}
        >
        <DialogContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
            <Stack direction='row' spacing={1} alignItems='center'>
                <LinkIcon sx={{ color: "#ff5a36", fontSize: 20 }} />
                <Typography sx={{ color: "#26364d", fontSize: "1.1rem", fontWeight: 700 }}>
                ERP-integraatio
                </Typography>
            </Stack>
            <IconButton aria-label='Sulje' onClick={onClose} size='small' sx={{ color: "#5f6368" }}>
                <CloseIcon fontSize='small' />
            </IconButton>
            </Stack>

            <Typography sx={{ color: "#667b9b", fontSize: "0.875rem", lineHeight: 1.55, mb: 2 }}>
            Yhdistä korjaamosi toiminnanohjausjärjestelmään. Kun integraatio on aktiivinen, jonon pituus päivittyy automaattisesti kalenteritasolla.
            </Typography>

            {selectedAdapter ? (
            <>
            <Button
                startIcon={<ArrowBackIcon fontSize='small' />}
                onClick={() => setSelectedAdapter(null)}
                sx={{ color: "#8198bb", fontSize: "0.75rem", justifyContent: "flex-start", mb: 1.5, px: 0, textTransform: "none" }}
            >
                Vaihda järjestelmä
            </Button>

            <Box sx={{ border: "1px solid #dce4ee", borderRadius: "12px", overflow: "hidden", mb: 2.5 }}>
                <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ backgroundColor: "#f7f9fb", p: 1.5 }}>
                    <Stack direction='row' spacing={1.25} alignItems='center'>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: "10px", backgroundColor: "#1e2d45", color: "#ff5a36" }}>
                            <LinkIcon />
                        </Box>
                        <Box>
                            <Typography sx={{ color: "#26364d", fontSize: "0.95rem", fontWeight: 700 }}>
                                {selectedDescription.name || selectedAdapter.provider}
                            </Typography>
                            <Typography sx={{ color: "#8198bb", fontSize: "0.75rem" }}>
                                {selectedDescription.createdBy || selectedAdapter.provider}
                            </Typography>
                        </Box>
                    </Stack>
                    <Button startIcon={<DescriptionOutlinedIcon fontSize='small' />} sx={{ color: "#ff5a36", fontSize: "0.75rem", textTransform: "none", whiteSpace: "nowrap" }}>
                        Integraatio-ohjeet
                    </Button>
                </Stack>
                <Typography sx={{ color: "#617694", fontSize: "0.75rem", lineHeight: 1.45, p: 1.5 }}>
                    {getLocalizedDescription(selectedAdapter)}
                </Typography>
            </Box>

            <Typography sx={{ color: "#8198bb", fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.03em", mb: 1.5 }}>
                YHTEYSTIEDOT
            </Typography>

            <Stack spacing={1.75}>
                {selectedFields.map((field) => (
                    <Box key={field.key}>
                        <Typography component='label' htmlFor={`erp-${field.key}`} sx={{ display: "block", color: "#405473", fontSize: "0.875rem", fontWeight: 600, mb: 0.75 }}>
                            {field.name}{field.required ? " *" : ""}
                        </Typography>
                        <TextField
                            id={`erp-${field.key}`}
                            fullWidth
                            size='small'
                            required={field.required}
                            type={field.type === "password" ? "password" : field.type === "url" ? "url" : "text"}
                            value={formValues[field.key] || ""}
                            onChange={(event) => setFormValues((values) => ({ ...values, [field.key]: event.target.value }))}
                            placeholder={field.placeholder || ""}
                            helperText={field.description}
                            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#f7f7f8", borderRadius: "8px" }, "& .MuiFormHelperText-root": { color: "#8198bb", mx: 0 } }}
                        />
                    </Box>
                ))}
            </Stack>

            {connectionResult && (
                <Typography sx={{ color: "#237a57", fontSize: "0.875rem", mt: 2 }}>
                    {connectionResult}
                </Typography>
            )}
            {connectionError && (
                <Typography sx={{ color: "#b42318", fontSize: "0.875rem", mt: 2 }}>
                    {connectionError}
                </Typography>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>
                <Button fullWidth variant='outlined' startIcon={isTesting ? <CircularProgress size={16} /> : <SettingsOutlinedIcon />} disabled={requiredFieldsMissing || isTesting} onClick={testConnection} sx={{ borderColor: "#dce4ee", color: "#405473", textTransform: "none" }}>
                    {isTesting ? "Testataan..." : "Testaa yhteys"}
                </Button>
                <Button fullWidth variant='contained' startIcon={<SaveOutlinedIcon />} disabled sx={{ backgroundColor: "#9aa1ac", textTransform: "none", "&:hover": { backgroundColor: "#9aa1ac" } }}>
                    Tallenna
                </Button>
            </Stack>
            </>
            ) : (
            <>
            <Typography sx={{ color: "#667b9b", fontSize: "0.75rem", fontWeight: 500, letterSpacing: "0.03em", mb: 1.25 }}>
            VALITSE JÄRJESTELMÄ
            </Typography>

            <Stack spacing={1}>
            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={26} sx={{ color: "#ff5a36" }} />
                </Box>
            ) : error ? (
                <Typography sx={{ color: "#b42318", fontSize: "0.875rem", py: 2 }}>
                {error}
                </Typography>
            ) : (
                erpAdapters.map((adapter) => {
                const description = adapter.description || {};
                return (
                <Box
                key={adapter.provider}
                component='button'
                type='button'
                onClick={() => selectAdapter(adapter)}
                sx={{
                    display: "block",
                    position: "relative",
                    width: "100%",
                    textAlign: "left",
                    color: "inherit",
                    backgroundColor: "#fff",
                    border: "1px solid #dce4ee",
                    borderRadius: "10px",
                    px: 1.5,
                    py: 1.25,
                    cursor: "pointer",
                    transition: "border-color 0.2s, background-color 0.2s",
                    "&:hover": { borderColor: "#aebed2", backgroundColor: "#fbfcfe" },
                }}
                >
                <Typography sx={{ color: "#26364d", fontSize: "0.875rem", fontWeight: 700, pr: 3 }}>
                    {description.name || adapter.provider}
                </Typography>
                <Typography sx={{ color: "#8198bb", fontSize: "0.75rem", mb: 0.35 }}>
                    {description.createdBy || adapter.provider}
                </Typography>
                <Typography sx={{ color: "#617694", fontSize: "0.75rem", lineHeight: 1.45, pr: 2 }}>
                    {getLocalizedDescription(adapter)}
                </Typography>
                <ChevronRightIcon sx={{ position: "absolute", right: 1.25, top: 1.5, color: "#b7cbe3", fontSize: 20 }} />
                </Box>
                );
                })
            )}
            </Stack>
            </>
            )}
        </DialogContent>
        </Dialog>
    );
}